import type { Editor } from "@tiptap/react";
import { type MutableRefObject, useEffect, useRef, useState } from "react";
import {
  copyTiptapEditorContent,
  type TiptapClipboardOptions,
} from "./tiptapClipboard";

export type TiptapTopBarCopyAction = {
  isCopied?: boolean;
  isDisabled?: boolean;
  onClick: () => void;
};

const clearCopyFeedbackTimeout = (
  timeoutRef: MutableRefObject<number | undefined>
) => {
  if (timeoutRef.current === undefined) {
    return;
  }

  window.clearTimeout(timeoutRef.current);
  timeoutRef.current = undefined;
};

export const useTiptapCopyAction = ({
  copySource,
  disabled,
  editor,
}: {
  copySource?: TiptapClipboardOptions["copySource"];
  disabled: boolean;
  editor: Editor | null;
}): TiptapTopBarCopyAction | null => {
  const [isCopied, setIsCopied] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const resetCopyStateTimeoutRef = useRef<number | undefined>();

  useEffect(() => {
    if (disabled || !editor) {
      setIsCopied(false);
      clearCopyFeedbackTimeout(resetCopyStateTimeoutRef);
    }
  }, [disabled, editor]);

  useEffect(
    () => () => {
      clearCopyFeedbackTimeout(resetCopyStateTimeoutRef);
    },
    []
  );

  if (!editor) {
    return null;
  }

  return {
    isCopied,
    isDisabled: disabled || isCopying,
    onClick: async () => {
      if (disabled || isCopying) {
        return;
      }

      setIsCopying(true);

      try {
        await copyTiptapEditorContent(editor, { copySource });
        setIsCopied(true);
        clearCopyFeedbackTimeout(resetCopyStateTimeoutRef);
        resetCopyStateTimeoutRef.current = window.setTimeout(() => {
          setIsCopied(false);
          resetCopyStateTimeoutRef.current = undefined;
        }, 1500);
      } catch {
        setIsCopied(false);
      } finally {
        setIsCopying(false);
      }
    },
  };
};
